/**
 * EditListingPage Component
 *
 * Page for editing an existing listing.
 */

import React, { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { Footer } from '../../../components/layout/Footer/Footer';
import { HostLayout } from '../components';
import { ListingWizard } from './components';
import { ListingFormData } from './hooks';
import { useAuth } from '../../../contexts';

// Fetch listing data from Firestore (returns null if not found or not owned by user)
const fetchListing = async (listingId: string, userId?: string): Promise<ListingFormData | null> => {
  const listingDoc = await getDoc(doc(db, 'listings', listingId));
  if (!listingDoc.exists()) return null;

  const d = listingDoc.data();

  // Verify ownership: only the host who owns this listing (or admin) can edit
  if (userId && d.hostId && d.hostId !== userId) {
    return null;
  }

  return {
    propertyType: d.propertyType || '',
    roomType: d.roomType || 'entire_place',
    title: d.title || '',
    description: d.description || '',
    bedrooms: d.capacity?.bedrooms || 0,
    beds: d.capacity?.beds || 0,
    bathrooms: d.capacity?.bathrooms || 0,
    maxGuests: d.capacity?.guests || 1,
    location: {
      country: d.location?.country || 'Lebanon',
      state: d.location?.state || '',
      area: '',
      city: d.location?.city || '',
      address: d.location?.address || '',
      fullAddress: d.location?.address || '',
      coordinates: d.location?.coordinates || { lat: 0, lng: 0 },
    },
    amenityIds: d.amenities || [],
    photos: (d.photos || []).map((p: { url: string }, i: number) => ({
      id: `p${i}`,
      url: p.url,
      order: i,
    })),
    pricing: {
      nightlyRate: d.pricing?.nightlyRate || 0,
      weekendRate: d.pricing?.weekendRate || null,
      weeklyDiscount: d.pricing?.weeklyDiscount || 0,
      monthlyDiscount: d.pricing?.monthlyDiscount || 0,
      cleaningFee: d.pricing?.cleaningFee || 0,
      securityDeposit: d.pricing?.securityDeposit || 0,
      currency: d.pricing?.currency || 'USD',
    },
    instantBooking: d.instantBook || false,
    minNights: d.minNights || 1,
    maxNights: d.maxNights || 30,
    checkInTime: d.checkInTime || '15:00',
    checkOutTime: d.checkOutTime || '11:00',
    advanceNotice: d.advanceNotice || 0,
    availabilityWindow: d.availabilityWindow || 90,
    houseRules: {
      smokingAllowed: d.houseRules?.smokingAllowed ?? false,
      petsAllowed: d.houseRules?.petsAllowed ?? false,
      partiesAllowed: d.houseRules?.partiesAllowed ?? false,
      // Mobile stores additionalRules as an array; the textarea needs a string.
      additionalRules: Array.isArray(d.houseRules?.additionalRules)
        ? d.houseRules.additionalRules.join('\n')
        : (d.houseRules?.additionalRules || ''),
    },
    cancellationPolicy: d.cancellationPolicy || 'moderate',
    status: d.status === 'published' ? 'active' : (d.status || 'draft'),
  };
};

export const EditListingPage: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const [listing, setListing] = useState<ListingFormData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadListing = async () => {
      if (!listingId || !user) return;

      try {
        setIsLoading(true);
        // Pass userId for ownership check (admins bypass via role check)
        const ownerCheck = user.role === 'admin' ? undefined : user.uid;
        const data = await fetchListing(listingId, ownerCheck);
        if (!data) {
          setError('Listing not found or you do not have permission to edit it');
        } else {
          setListing(data);
        }
      } catch {
        setError('Failed to load listing');
      } finally {
        setIsLoading(false);
      }
    };

    loadListing();
  }, [listingId, user]);

  // Check if user is a host
  if (!authLoading && user && user.role !== 'host' && user.role !== 'admin') {
    return <Navigate to="/become-host" replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !listing) {
    return (
      <HostLayout>
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertIcon className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {error || 'Listing not found'}
          </h2>
          <p className="text-gray-600 mb-6">
            The listing you're looking for doesn't exist or you don't have permission to edit it.
          </p>
          <Link
            to="/host/listings"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            Back to listings
          </Link>
        </div>
      </HostLayout>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Simplified header for wizard mode */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <Link to="/host" className="flex items-center">
            <img src="/logos/logo-wide.png" alt="DARAK" className="h-8 w-auto object-contain" />
          </Link>
        </div>
      </div>

      {/* Wizard Content */}
      <div className="flex-1 max-w-5xl mx-auto px-4 py-8 w-full">
        <ListingWizard
          mode="edit"
          listingId={listingId}
          initialData={listing}
        />
      </div>

      <Footer />
    </div>
  );
};

// Icons
const AlertIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const LoadingSpinner: React.FC = () => (
  <svg className="w-8 h-8 animate-spin text-primary-500" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

export default EditListingPage;
